import { createSlice } from '@reduxjs/toolkit'
import Sentry from '../utils/sentry'
import { API } from 'src/utils/interceptor'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'
import { normalizeStockListExpected } from 'src/utils/stockOnHand'
import { stockTakeCreateRequestPath } from 'src/utils/stockTakeCreateForm'

const normalizeAggregateValue = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeStockTake = stockTake => {
  if (!stockTake || typeof stockTake !== 'object') return stockTake

  return {
    ...stockTake,
    totalStockValue: normalizeAggregateValue(stockTake.totalStockValue),
    totalWastageValue: normalizeAggregateValue(stockTake.totalWastageValue),
    expectedStockValue: normalizeStockListExpected(
      stockTake.expectedStockValue,
    ),
  }
}

const normalizeStockTakeList = payload => {
  if (!Array.isArray(payload)) return []
  return payload.map(normalizeStockTake)
}

const pickStockTakeMetadata = payload => {
  const metadataKeys = [
    'siteName',
    'siteManager',
    'lineManager',
    'countedByName',
    'reasonForCount',
    'countNotes',
  ]
  return metadataKeys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload || {}, key)) {
      acc[key] = payload[key]
    }
    return acc
  }, {})
}

const mapStockTakeUpdatePayload = payload => ({
  ...payload,
  ...pickStockTakeMetadata(payload),
  unlockReason: payload?.unlockReason ?? undefined,
})

const mapStockTakeCreatePayload = payload => {
  const next = {
    ...payload,
    ...pickStockTakeMetadata(payload),
  }
  const name = String(payload?.name || '').trim()
  if (name) next.name = name
  else delete next.name
  if (Array.isArray(payload?.supplierIds) && payload.supplierIds.length) {
    next.supplierIds = payload.supplierIds
  } else {
    delete next.supplierIds
  }
  return next
}

const mapStockTakeProductPayload = product => ({
  ...product,
  wastageComment: product?.wastageComment ?? '',
  conversionId: product?.conversionId ?? null,
  conversionName: product?.conversionName ?? '',
  conversionUnit: product?.conversionUnit ?? '',
  conversionFactor: product?.conversionFactor ?? undefined,
  conversionBaseUnit: product?.conversionBaseUnit ?? '',
  conversionInputQty: product?.conversionInputQty ?? undefined,
  conversionAppliedAt: product?.conversionAppliedAt ?? undefined,
})

const mapSaveAllChangesPayload = payload => ({
  ...payload,
  metadata: payload?.metadata ?? undefined,
  soldRecipes: payload?.soldRecipes ?? undefined,
  wastedRecipes: payload?.wastedRecipes ?? undefined,
  wastedRecipeUsage: payload?.wastedRecipeUsage ?? undefined,
  toAdd: Array.isArray(payload?.toAdd)
    ? payload.toAdd.map(mapStockTakeProductPayload)
    : [],
  toUpdate: Array.isArray(payload?.toUpdate)
    ? payload.toUpdate.map(mapStockTakeProductPayload)
    : [],
})

const stocksSlice = createSlice({
  name: 'stockTakes',
  initialState: {
    stockTakes: [],
    isLoading: false,
    stockTakeProducts: [],
    buttonLoading: { stockTake: '', isLoading: false, buttonType: '' },
  },
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setStockTakes: (state, action) => {
      state.stockTakes = normalizeStockTakeList(action.payload)
    },
    editStockTake: (state, action) => {
      const index = state.stockTakes.findIndex(
        stockTake => stockTake._id === action.payload._id,
      )
      if (index !== -1)
        state.stockTakes[index] = normalizeStockTake(action.payload)
    },
    updateStockTakes: (state, action) => {
      state.stockTakes.unshift(normalizeStockTake(action.payload))
    },
    deleteStock: (state, action) => {
      state.stockTakes = state.stockTakes.filter(
        stockTake => stockTake._id !== action.payload,
      )
    },
    setStockTakeProducts: (state, action) => {
      state.stockTakeProducts = action.payload
    },
    setButtonLoading: (state, action) => {
      state.buttonLoading = action.payload
    },
  },
})

export const {
  setIsLoading,
  setStockTakes,
  editStockTake,
  updateStockTakes,
  deleteStock,
  setStockTakeProducts,
  setButtonLoading,
} = stocksSlice.actions

export const fetchStockTakes = venueId => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    const data = await API.get('api2', `venue/${venueId}/stocks`)
    dispatch(setStockTakes(normalizeStockTakeList(data)))
    dispatch(setIsLoading(false))
  } catch (e) {
    dispatch(setIsLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const updateStockTake =
  (venueId, data, buttonType) => async dispatch => {
    try {
      dispatch(
        setButtonLoading({
          stockTake: data._id,
          isLoading: true,
          buttonType: buttonType,
        }),
      )
      const updatedStockTake = await API.post(
        'api2',
        `venue/${venueId}/stocks`,
        {
          body: mapStockTakeUpdatePayload(data),
        },
      )
      dispatch(editStockTake(updatedStockTake))
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.STOCK_TAKE_SAVED,
      })
      dispatch(
        setButtonLoading({ stockTake: '', isLoading: false, buttonType: '' }),
      )
    } catch (e) {
      dispatch(
        setButtonLoading({ stockTake: '', isLoading: false, buttonType: '' }),
      )
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const createStock = (data, venueId) => async dispatch => {
  try {
    const newDateOfStock = await API.post(
      'api2',
      stockTakeCreateRequestPath(venueId, data),
      {
        body: mapStockTakeCreatePayload(data),
      },
    )
    dispatch(updateStockTakes(newDateOfStock))
    ollieFindingsSaved({
      venueId,
      event: OLLIE_FINDINGS_WRITE_EVENTS.STOCK_TAKE_SAVED,
    })
    if (venueId) {
      emitGrowthEngineGuidedLaneProgress(
        venueId,
        GROWTH_ENGINE_GUIDED_LANE_REASONS.STOCK_TAKE_CREATED,
      )
    }
    return newDateOfStock
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const deleteStockTake =
  (venueId, stockTake, buttonType) => async dispatch => {
    try {
      dispatch(
        setButtonLoading({
          stockTake: stockTake._id,
          isLoading: true,
          buttonType: buttonType,
        }),
      )
      await API.del('api2', `venue/${venueId}/stocks/${stockTake._id}`)
      dispatch(deleteStock(stockTake._id))
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.STOCK_TAKE_SAVED,
      })
      dispatch(
        setButtonLoading({ stockTake: '', isLoading: false, buttonType: '' }),
      )
    } catch (e) {
      dispatch(
        setButtonLoading({ stockTake: '', isLoading: false, buttonType: '' }),
      )
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const addVenueProductLineToStockTake =
  (venueId, stockTakeId, venueProductId) => async () => {
    try {
      const created = await API.post(
        'api2',
        `venue/${venueId}/stocks/${stockTakeId}/products/from-venue-product`,
        {
          body: { venueProductId, platform: 'dashboard' },
        },
      )
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.STOCK_TAKE_SAVED,
      })
      return created
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const fetchOrderToParSuggestions =
  (venueId, stockTakeId) => async () => {
    try {
      return await API.get(
        'api2',
        `venue/${venueId}/stocks/${stockTakeId}/order-to-par?platform=dashboard`,
      )
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return null
    }
  }

export const addOrderToParToBasket =
  (venueId, stockTakeId, lines) => async () => {
    try {
      return await API.post(
        'api2',
        `venue/${venueId}/stocks/${stockTakeId}/order-to-par/add-to-basket`,
        {
          body: {
            platform: 'dashboard',
            ...(Array.isArray(lines) ? { lines } : {}),
          },
        },
      )
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return null
    }
  }

export const fetchStockTakeProducts =
  (venueId, stockTakeId) => async dispatch => {
    try {
      dispatch(setIsLoading(true))
      const stockTakeProducts = await API.get(
        'api2',
        `venue/${venueId}/stocks/${stockTakeId}/products`,
      )
      dispatch(setStockTakeProducts(stockTakeProducts))
      dispatch(setIsLoading(false))
    } catch (e) {
      dispatch(setIsLoading(false))
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const saveAllChanges = (venueId, data, buttonType) => async dispatch => {
  try {
    dispatch(setButtonLoading({ isLoading: true, buttonType: buttonType }))
    const payload = mapSaveAllChangesPayload(data)
    await API.post(
      'api2',
      `venue/${venueId}/stocks/${data.stockTakeId}/products`,
      {
        body: payload,
      },
    )
    ollieFindingsSaved({
      venueId,
      event: OLLIE_FINDINGS_WRITE_EVENTS.STOCK_TAKE_SAVED,
    })
    dispatch(
      setButtonLoading({ stockTake: '', isLoading: false, buttonType: '' }),
    )
    const refreshedProducts = await API.get(
      'api2',
      `venue/${venueId}/stocks/${data.stockTakeId}/products`,
    )
    dispatch(setStockTakeProducts(refreshedProducts))
    return true
  } catch (e) {
    dispatch(
      setButtonLoading({ stockTake: '', isLoading: false, buttonType: '' }),
    )
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export default stocksSlice
